import { Config } from 'common/config'
import { checkStatus, parseBody } from 'utils/http'

export interface RecordedSessionChunk {
	sessionOrdinal: number
	lengthSeconds?: number
	sizeMegabytes?: number
	fileName?: string
	status?: string
}

export interface RecordingDto {
	sessionId: string
	status: string
	chunks: RecordedSessionChunk[]
}

export interface RecordingInitializationStatusDto {
	initialized: boolean
	status?: RecordingInitializationLifecycleStatus
	error?: string
}

export type RecordingInitializationLifecycleStatus =
	| 'not_initialized'
	| 'ready'
	| 'starting'
	| 'recording'
	| 'paused'
	| 'paused_track_lost'
	| 'finalizing'
	| 'completed'
	| 'error'

export interface RecordingInitializationStatus {
	initialized: boolean
	status: RecordingInitializationLifecycleStatus
	error?: string
}

export const DEFAULT_RECORDING_INITIALIZATION_STATUS: RecordingInitializationStatus =
	{
		initialized: false,
		status: 'not_initialized',
	}

export const resolveRecordingInitializationStatus = (
	status:
		| boolean
		| RecordingInitializationStatusDto
		| RecordingInitializationStatus
		| undefined
		| null
): RecordingInitializationStatus => {
	if (typeof status === 'boolean') {
		return {
			initialized: status,
			status: status ? 'ready' : 'not_initialized',
		}
	}

	if (!status) {
		return DEFAULT_RECORDING_INITIALIZATION_STATUS
	}

	return {
		initialized: status.initialized,
		status: status.status || (status.initialized ? 'ready' : 'not_initialized'),
		error: status.error,
	}
}

export interface RecordingClientOptions {
	baseUrl?: string
	defaultInitErrorCode?: string
	/** How long a request may take before it fails (default 30 s). */
	timeoutMs?: number
}

/**
 * session-planning answers within about 21 s even when it retries the
 * recorder (5 s per attempt, 2 retries); a request still open after this is
 * lost, and must not keep the trainer's recording controls waiting.
 */
const REQUEST_TIMEOUT_MS = 30_000

type OrchestratedRecordingRequestBody = {
	roomId: string
}

type SessionPlanningErrorPayload = {
	message?: string
	error?: string
	code?: string
	status?: number
}

export class RecordingServiceError extends Error {
	constructor(
		message: string,
		public statusCode?: number,
		public code?: string,
		public details?: unknown
	) {
		super(message)
		this.name = 'RecordingServiceError'
	}

	isInitAlreadyDone(): boolean {
		return this.code === 'RECSERV001'
	}

	static fromUnknown(error: unknown): RecordingServiceError {
		if (error instanceof RecordingServiceError) {
			return error
		}

		const payload = (error || {}) as SessionPlanningErrorPayload
		const message = payload.message || payload.error || 'Request failed'
		return new RecordingServiceError(
			message,
			payload.status,
			payload.code,
			error
		)
	}
}

export class RecordingClient {
	private readonly baseApi: string
	private readonly timeoutMs: number

	constructor(options: RecordingClientOptions = {}) {
		this.baseApi =
			options.baseUrl || `${Config.sessionPlanningURL}/sessions/recording`
		this.timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS
	}

	private async request<T>(path: string, init?: RequestInit): Promise<T> {
		const controller = new AbortController()
		const timer = setTimeout(() => controller.abort(), this.timeoutMs)
		try {
			const response = await fetch(`${this.baseApi}${path}`, {
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
				...init,
				signal: controller.signal,
			})

			const checkedResponse = await checkStatus(response)
			if (checkedResponse instanceof Response) {
				return (await parseBody(checkedResponse)) as T
			}

			return checkedResponse as T
		} catch (error) {
			if (controller.signal.aborted) {
				throw new RecordingServiceError(
					`Recording service did not answer within ${this.timeoutMs} ms`,
					504,
					'TIMEOUT',
					error
				)
			}
			throw RecordingServiceError.fromUnknown(error)
		} finally {
			clearTimeout(timer)
		}
	}

	async getRecording(sessionId: string): Promise<RecordingDto> {
		return this.request<RecordingDto>(`/${sessionId}`)
	}

	async getRecordingInitializationStatus(
		sessionId: string
	): Promise<boolean | RecordingInitializationStatus> {
		const result = await this.request<RecordingInitializationStatusDto>(
			`/initialization/${sessionId}`
		)

		return resolveRecordingInitializationStatus(result)
	}

	async initRecording(sessionId: string): Promise<void> {
		await this.request<void>(`/init/${sessionId}`, {
			method: 'POST',
		})
	}

	async startRecording(sessionId: string, roomId: string): Promise<void> {
		await this.request<void>(`/start/${sessionId}/${roomId}`, {
			method: 'PATCH',
		})
	}

	async stopRecording(sessionId: string): Promise<void> {
		await this.request<void>(`/stop/${sessionId}`, {
			method: 'PATCH',
		})
	}
}

export function createRecordingClient(baseUrl?: string): RecordingClient {
	return new RecordingClient({ baseUrl })
}
